<template>
  <div class="ms-form ms-form--narrow">
    <q-linear-progress v-if="this.loading" indeterminate/>
    <template v-else>
      <div v-if="this.canEdit && this.data?.onSaved" class="review__hint">
        Перед загрузкой АПП оцените работу проектировщика. После продолжения откроется форма документа.
      </div>
      <div v-if="this.isLocked" class="review__meta">
        <div class="review__meta-title">Оценка сохранена, изменить нельзя</div>
        <div v-if="this.reviewEmployeeFio">
          Оценил: {{ this.reviewEmployeeFio }}
          <span v-if="!!this.reviewDate">({{ this.reviewDate }})</span>
        </div>
        <div v-if="this.designerFio">Проектировщик: {{ this.designerFio }}</div>
      </div>
      <q-form @submit="this.onSubmit" class="review__form">
        <div class="review__stars">
          <q-rating
              :model-value="this.score || 0"
              :max="5"
              size="2em"
              color="amber"
              :readonly="!this.canEdit || this.saving"
              @update:model-value="this.onScoreChange"
          />
        </div>
        <div class="review__scale">
          <span>1 — плохо</span>
          <span>5 — отлично</span>
        </div>

        <div v-if="this.score === 4" class="review__branch">
          <CustomInput
              :value="this.improveComment"
              label="Что необходимо улучшить?"
              type="textarea"
              :required="true"
              :disable="!this.canEdit || this.saving"
              @change_input="(value) => this.improveComment = value"
          />
        </div>

        <div v-if="this.score >= 1 && this.score <= 3" class="review__branch">
          <div class="review__question">Что пошло не так?</div>
          <div class="review__tags">
            <q-btn
                v-for="tag in this.tagCatalog"
                :key="tag.tag_code"
                unelevated
                no-caps
                :outline="!this.isTagSelected(tag.tag_code)"
                :color="this.isTagSelected(tag.tag_code) ? 'primary' : 'grey-7'"
                :disable="!this.canEdit || this.saving"
                class="review__tag"
                @click="this.toggleTag(tag.tag_code)"
            >
              {{ tag.tag_name }}
            </q-btn>
          </div>
          <CustomInput
              :value="this.detailComment"
              label="Опишите детальнее (опционально)"
              type="textarea"
              :disable="!this.canEdit || this.saving"
              @change_input="(value) => this.detailComment = value"
          />
        </div>

        <CustomModalFooter
            :onCancel="this.closeModal"
            :btn-loading="this.saving"
            :hideSubmitBtn="!this.canEdit"
            submit-btn-text="Продолжить"
            cancel-btn-text="Закрыть"
        />
      </q-form>
    </template>
  </div>
</template>
<script>

import CustomInput from "@/components/form/CustomInput";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import {getProjectReview} from "@/views/ClientRequests/remontDocs/services";
import {useCustomSnackbar} from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: "project-review",
  components: {CustomInput, CustomModalFooter},
  props: ['data', 'closeModal'],
  data() {
    return {
      loading: false,
      saving: false,
      isEditAccess: false,
      projectReviewId: null,
      reviewEmployeeFio: '',
      designerFio: '',
      reviewDate: '',
      score: null,
      improveComment: '',
      detailComment: '',
      tagCodes: [],
      tagCatalog: [],
    };
  },
  computed: {
    isLocked() {
      return !!this.score && !this.isEditAccess;
    },
    canEdit() {
      return !!this.isEditAccess;
    },
  },
  methods: {
    isTagSelected(tag_code) {
      return this.tagCodes.includes(tag_code);
    },
    toggleTag(tag_code) {
      if (!this.canEdit || this.saving) return;
      if (this.isTagSelected(tag_code)) {
        this.tagCodes = this.tagCodes.filter((code) => code !== tag_code);
        return;
      }
      this.tagCodes = [...this.tagCodes, tag_code];
    },
    onScoreChange(value) {
      if (!this.canEdit) return;
      this.score = value || null;
      if (this.score === 5) {
        this.improveComment = '';
        this.detailComment = '';
        this.tagCodes = [];
      }
      if (this.score === 4) {
        this.detailComment = '';
        this.tagCodes = [];
      }
      if (this.score >= 1 && this.score <= 3) {
        this.improveComment = '';
      }
    },
    setReviewData(res) {
      this.projectReviewId = res?.project_review_id || null;
      this.isEditAccess = !!res?.is_edit_access;
      this.reviewEmployeeFio = res?.review_employee_fio || '';
      this.designerFio = res?.techproject_employee_fio || '';
      this.reviewDate = res?.review_date || '';
      this.score = res?.score || null;
      this.improveComment = res?.improve_comment || '';
      this.detailComment = res?.detail_comment || '';
      this.tagCodes = Array.isArray(res?.tag_codes) ? [...res.tag_codes] : [];
      this.tagCatalog = Array.isArray(res?.tag_catalog) ? res.tag_catalog : [];
    },
    async getReview() {
      this.loading = true;
      const res = await getProjectReview(this.data?.client_request_id);
      if (!res) {
        this.loading = false;
        return this.showErrorSnackbar('Не удалось загрузить форму оценки');
      }
      this.setReviewData(res);

      const onSaved = this.data?.onSaved;
      if (typeof onSaved === 'function' && this.score) {
        onSaved(null);
        return;
      }
      this.loading = false;
      if (typeof onSaved === 'function' && !this.canEdit) {
        this.showErrorSnackbar('Нет права выставлять оценку проекта');
        this.closeModal();
      }
    },
    reviewPayload() {
      return {
        score: this.score,
        improve_comment: this.score === 4 ? this.improveComment : '',
        detail_comment: this.score <= 3 ? this.detailComment : '',
        tag_codes: this.score <= 3 ? this.tagCodes : [],
      };
    },
    onSubmit() {
      if (!this.canEdit) return;
      if (!this.score) {
        return this.showErrorSnackbar('Оцените качество проектной документации');
      }
      if (this.score <= 3 && !this.tagCodes.length) {
        return this.showErrorSnackbar('Выберите хотя бы одну причину');
      }
      if (this.score === 4 && !this.improveComment?.trim()) {
        return this.showErrorSnackbar('Укажите, что необходимо улучшить');
      }

      const onSaved = this.data?.onSaved;
      if (typeof onSaved === "function") {
        onSaved(this.reviewPayload());
        return;
      }
      this.closeModal();
    },
  },
  mounted() {
    this.getReview();
  },
  setup() {
    const {showErrorSnackbar} = useCustomSnackbar();
    return {showErrorSnackbar};
  },
};
</script>

<style scoped lang="scss">
// Ширина — общая (ms-form--narrow в theme.scss)
.review__meta {
  font-size: 13px;
  line-height: 18px;
  color: var(--ms-text-secondary);
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.review__meta-title {
  font-weight: 600;
  color: var(--ms-text);
}

.review__hint {
  font-size: 13px;
  line-height: 18px;
  color: var(--ms-text-secondary);
  background: var(--ms-bg);
  border-radius: var(--ms-radius-sm);
  padding: 10px 12px;
  margin-bottom: 12px;
}

.review__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review__question {
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
}

.review__stars {
  display: flex;
  justify-content: flex-start;

  :deep(.q-rating) {
    gap: 6px;
  }
}

.review__scale {
  display: flex;
  justify-content: space-between;
  max-width: 220px;
  font-size: 12px;
  color: var(--ms-muted);
  margin-top: -8px;
}

.review__branch {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.review__tag {
  border-radius: 999px;
}
</style>
